import React from 'react';
import { useTranslation } from 'react-i18next';

import type { HomePeriod } from '../../mock-data/types';
import type { CustomRangeChoice, IntervalOption } from './components';
import {
  HOME_INTERVAL_OPTIONS,
  MAX_CUSTOM_DAYS,
  MIN_CUSTOM_DAYS,
  customDays,
  customPeriod,
  customPeriodLabel,
  localToday,
} from './stats-mapping';

/**
 * The Home and Stats period picker: the preset ranges (labels translated here, at the render site, so they follow a
 * language switch; each branch wraps a literal so the extractor harvests it), the picked custom range as a hidden
 * option so the pill names it ("Sep 1 – Sep 30"), and the custom-range row (the viewer's calendar dates, ending today
 * at the latest in their calendar or in UTC, {@link MIN_CUSTOM_DAYS} to {@link MAX_CUSTOM_DAYS} days).
 */
export const usePeriodPicker = (
  period: HomePeriod,
  onChange: (period: HomePeriod) => void,
): { options: IntervalOption<HomePeriod>[]; custom: CustomRangeChoice } => {
  const { t, i18n } = useTranslation();
  const range = customDays(period);

  const options = React.useMemo<IntervalOption<HomePeriod>[]>(() => {
    const presets: IntervalOption<HomePeriod>[] = HOME_INTERVAL_OPTIONS.map((option) => {
      switch (option.value) {
        case 'TODAY':
          return { ...option, label: t('Today') };
        case 'YESTERDAY':
          return { ...option, label: t('Yesterday') };
        case '7D':
          return { ...option, label: t('Last 7 days') };
        case '30D':
          return { ...option, label: t('Last 30 days') };
        case '90D':
          return { ...option, label: t('Last 90 days') };
        case '6M':
          return { ...option, label: t('Last 6 months') };
        case '1Y':
          return { ...option, label: t('Last year') };
        default:
          return option;
      }
    });
    const label = customPeriodLabel(period, i18n.language);
    return label === null ? presets : [...presets, { value: period, label, searchOnly: true }];
  }, [t, i18n.language, period]);

  const custom: CustomRangeChoice = {
    value: range ? { from: range.from, to: range.to } : null,
    // Today in the viewer's calendar or in UTC, whichever is later: a range shared from the Website tab (UTC days) can
    // end on the UTC day, and must reopen here as a valid one.
    max: [localToday(), new Date().toISOString().slice(0, 10)].sort()[1],
    maxDays: MAX_CUSTOM_DAYS,
    minDays: MIN_CUSTOM_DAYS,
    onApply: (from, to) => onChange(customPeriod(from, to)),
  };

  return { options, custom };
};

export default usePeriodPicker;
